import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Loader } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';

interface MyCertificate {
  id: string;
  title: string;
  issuedAt?: string;
  status?: string;
  code?: string;
  credentialType?: string | null;
}

function statusBadgeVariant(status?: string): 'success' | 'warning' | 'danger' | 'default' {
  switch (status) {
    case 'issued':
    case 'active':
      return 'success';
    case 'expired':
      return 'warning';
    case 'revoked':
      return 'danger';
    default:
      return 'default';
  }
}

function statusLabel(status?: string): string {
  switch (status) {
    case 'issued':
      return 'Emitida';
    case 'expired':
      return 'Expirada';
    case 'revoked':
      return 'Revocada';
    default:
      return status ?? '—';
  }
}

function formatIssuedDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Vista estudiante «Mis certificados» (Laravel: mis-credenciales.index → solo certificados, no badges). */
export function MisCertificadosPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { canAccessAdmin } = usePermissions();
  const backTo = canAccessAdmin ? '/administracion?tab=general' : '/dashboard';
  const backLabel = canAccessAdmin ? 'Volver a Administración' : 'Volver al panel principal';

  const { data, isLoading, error } = useQuery({
    queryKey: ['student', 'certificates'],
    queryFn: () => api<{ data: MyCertificate[] }>('/student/certificates'),
    enabled: isAuthenticated,
  });

  const certificates = data?.data ?? [];
  const count = certificates.length;
  const countLabel = count === 1 ? '1 Credencial' : `${count} Credenciales`;

  return (
    <div className="lms-certificates lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to={backTo} label={backLabel} />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Mis certificados</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Certificados y diplomas obtenidos
          </p>
        </div>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading || authLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <section className="lms-certificates-glass relative overflow-hidden rounded-xl p-8 shadow-sm md:p-12">
          <div
            className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand-100/50 blur-3xl"
            aria-hidden="true"
          />

          <div className="relative z-10">
            <div className="mb-10 flex flex-wrap items-center justify-between gap-4 md:mb-12">
              <h2 className="text-2xl font-semibold text-dash-on-surface">Tus certificados</h2>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-dash-on-surface-variant">
                  {countLabel}
                </span>
                <Link
                  to="/mis-certificados/insignias"
                  className="text-sm font-medium text-dash-primary hover:underline"
                >
                  Ver insignias
                </Link>
              </div>
            </div>

            {certificates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center md:py-20">
                <div className="relative mb-8">
                  <div className="lms-certificates-float flex h-48 w-48 items-center justify-center rounded-full border border-dash-outline-variant/30 bg-secondary-50">
                    <span
                      className="material-symbols-outlined text-[5rem] text-dash-primary opacity-20"
                      style={{ fontVariationSettings: "'wght' 200" }}
                      aria-hidden="true"
                    >
                      workspace_premium
                    </span>
                  </div>
                  <div className="absolute -bottom-2 -right-2 flex h-16 w-16 items-center justify-center rounded-full border border-dash-outline-variant/10 bg-white shadow-lg">
                    <span className="material-symbols-outlined text-[1.75rem] text-dash-secondary" aria-hidden="true">
                      search_off
                    </span>
                  </div>
                </div>
                <h3 className="mb-3 text-2xl font-semibold text-dash-on-surface">Sin certificados</h3>
                <p className="mx-auto max-w-[22.5rem] text-dash-on-surface-variant">
                  Cuando obtengas una credencial, aparecerá aquí. Sigue avanzando en tus cursos para desbloquear nuevos
                  logros académicos.
                </p>
                <Link
                  to="/mis-cursos"
                  className="mt-8 inline-flex items-center gap-2 rounded-lg bg-dash-primary px-8 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90"
                >
                  Explorar cursos
                  <span className="material-symbols-outlined text-[1.125rem]" aria-hidden="true">
                    open_in_new
                  </span>
                </Link>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2">
                {certificates.map((cert) => (
                  <article
                    key={cert.id}
                    className="flex h-full flex-col rounded-xl border border-dash-outline-variant/60 bg-white/80 p-6 backdrop-blur-sm transition-shadow hover:shadow-md"
                  >
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <Badge variant={statusBadgeVariant(cert.status)}>{statusLabel(cert.status)}</Badge>
                      {cert.credentialType && (
                        <span className="text-xs font-medium text-dash-on-surface-variant">{cert.credentialType}</span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-dash-primary">{cert.title}</h3>
                    <p className="mt-1 text-sm text-dash-on-surface-variant">
                      Emitido {formatIssuedDate(cert.issuedAt)}
                    </p>
                    {cert.code && <p className="mt-1 font-mono text-xs text-secondary-400">{cert.code}</p>}
                    <div className="mt-auto flex gap-2 pt-5">
                      <Link
                        to={`/mis-certificados/${cert.id}`}
                        className="inline-flex flex-1 items-center justify-center rounded-lg border border-dash-outline-variant px-3 py-2 text-sm font-medium text-dash-on-surface transition-colors hover:bg-secondary-50"
                      >
                        Ver
                      </Link>
                      <a
                        href={`/api/student/certificates/${cert.id}/pdf`}
                        className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-dash-primary px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
                      >
                        <span className="material-symbols-outlined text-base" aria-hidden="true">
                          download
                        </span>
                        PDF
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
